// Cliente HTTP simples para /api/debug/*
// Usa cookies (credentials: 'include') para autenticação JWT compartilhada.

import type {
  RuntimeConfig,
  SessionDetail,
  SessionEvent,
  SessionEventType,
  SessionMeta,
  SpecListItem,
  User,
} from '../types';

const API_BASE = '/api/debug';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  if (!res.ok) {
    let body: { error?: string } = {};
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, body.error || `HTTP ${res.status}`);
  }

  return (await res.json()) as T;
}

export const api = {
  // Auth
  async getMe(): Promise<User> {
    return request<User>('/me');
  },

  // Sessions
  async listSessions(opts: {
    status?: string;
    specId?: string;
    q?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ total: number; offset: number; limit: number; sessions: SessionMeta[] }> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.specId) params.set('specId', opts.specId);
    if (opts.q) params.set('q', opts.q);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return request(`/sessions${qs ? '?' + qs : ''}`);
  },

  async getSession(id: string): Promise<SessionDetail> {
    return request(`/sessions/${id}`);
  },

  async getEvents(
    id: string,
    opts: { limit?: number; offset?: number; types?: SessionEventType[]; reverse?: boolean } = {},
  ): Promise<{ events: SessionEvent[]; total: number }> {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    if (opts.types?.length) params.set('types', opts.types.join(','));
    if (opts.reverse) params.set('reverse', 'true');
    const qs = params.toString();
    return request(`/sessions/${id}/events${qs ? '?' + qs : ''}`);
  },

  async listSpecs(): Promise<{ specs: SpecListItem[] }> {
    return request('/specs');
  },

  // ─── Settings (providers + transcrição) ─────────────────────────────────
  // Vivem no Studio (/admin/providers e /admin/transcricao). O agent não
  // persiste mais nada localmente — hidrata em memória via providerCache.

  // Runtime config — estado vigente em memória do agent
  async getRuntimeConfig(): Promise<RuntimeConfig> {
    return request<RuntimeConfig>('/runtime-config');
  },

  async reloadRuntimeConfig(): Promise<{ success: boolean; hydratedAt: string | null; error?: string }> {
    return request('/runtime-config/reload', { method: 'POST' });
  },

  // Models catalog + mode config
  async getModelCatalog(refresh = false): Promise<{
    models: Array<{
      provider: 'anthropic' | 'openai' | 'google';
      id: string;
      displayName: string;
      supportsTools: boolean;
      deprecated?: boolean;
      inputPerMTok?: number;
      outputPerMTok?: number;
    }>;
    fetchedAt: string;
    errors: Array<{ provider: string; reason: string }>;
  }> {
    return request(`/models/catalog${refresh ? '?refresh=1' : ''}`);
  },

  async refreshModelCatalog(): Promise<{
    models: Array<{
      provider: 'anthropic' | 'openai' | 'google';
      id: string;
      displayName: string;
      supportsTools: boolean;
      deprecated?: boolean;
      inputPerMTok?: number;
      outputPerMTok?: number;
    }>;
    fetchedAt: string;
    errors: Array<{ provider: string; reason: string }>;
  }> {
    return request('/models/catalog/refresh', { method: 'POST' });
  },

  // Transcribe (multipart — não usa request())
  async transcribe(audioBlob: Blob): Promise<{ text: string }> {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    const res = await fetch(`${API_BASE}/transcribe`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      let body: { error?: string } = {};
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, body.error || `HTTP ${res.status}`);
    }
    return (await res.json()) as { text: string };
  },

  // Files (multipart — não usa request())
  async uploadFile(
    sessionId: string,
    file: File,
  ): Promise<{
    success: boolean;
    file: { filename: string; originalName: string; size: number; type: string; path: string };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/sessions/${sessionId}/files`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      let body: { error?: string } = {};
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, body.error || `HTTP ${res.status}`);
    }
    return await res.json();
  },

  // Actions
  async sendMessage(
    id: string,
    content: string,
    mode?: 'ask' | 'plan' | 'implement',
  ): Promise<{ success: boolean }> {
    return request(`/sessions/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, mode }),
    });
  },

  async pause(id: string): Promise<{ success: boolean }> {
    return request(`/sessions/${id}/pause`, { method: 'POST' });
  },

  async resume(id: string): Promise<{ success: boolean }> {
    return request(`/sessions/${id}/resume`, { method: 'POST' });
  },

  async stop(id: string): Promise<{ success: boolean }> {
    return request(`/sessions/${id}/stop`, { method: 'POST' });
  },

  async reactivate(id: string): Promise<{ success: boolean }> {
    return request(`/sessions/${id}/reactivate`, { method: 'POST' });
  },

  async archive(id: string): Promise<{ success: boolean }> {
    return request(`/sessions/${id}/archive`, { method: 'POST' });
  },
};

export { ApiError };
